import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import type { Wishlist, Listing } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";
import { ListingCard } from "@/components/listings/ListingCard";

interface SavedSearch {
  id: string;
  name: string | null;
  params: string;
  createdAt: string;
}

const TRACKER_QUERY = gql`
  query Tracker {
    wishlists {
      id
      maxPrice
      notes
      notifyOnListing
      createdAt
      game {
        id
        title
        imageUrl
        thumbnailUrl
        yearPublished
        playerRange
        complexityLabel
        activeListingsCount
        lowestPrice
      }
    }
    mySavedListings {
      id
      price
      condition
      conditionLabel
      language
      languageLabel
      status
      createdAt
      imageUrls
      displayTitle
      displayImageUrl
      shippingAvailable
      shippingCost
      location
      boundingBox {
        yMin
        xMin
        yMax
        xMax
      }
      siblingListings {
        id
        status
        gameTitle
        price
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
      }
      fomoBadges {
        savingsVsShops
        savingsPercent
        cheapestShopPrice
        uniqueInMarketplace
        wishlistCount
        recent
        priceDropped
        previousPrice
      }
      game {
        id
        title
        imageUrl
        thumbnailUrl
        bggRating
        displayRating
        ratingSource
      }
      seller {
        id
        displayName
        avatarUrl
        rating
        ratingsCount
      }
    }
    mySavedSearches {
      id
      name
      params
      createdAt
    }
  }
`;

const TOGGLE_WISHLIST_MUTATION = gql`
  mutation ToggleWishlist($gameId: ID!) {
    toggleWishlist(gameId: $gameId) {
      removed
    }
  }
`;

const UPDATE_WISHLIST_MUTATION = gql`
  mutation UpdateWishlist($id: ID!, $notifyOnListing: Boolean) {
    updateWishlist(id: $id, notifyOnListing: $notifyOnListing) {
      wishlist {
        id
        notifyOnListing
      }
      errors
    }
  }
`;

const TOGGLE_SAVED_LISTING_MUTATION = gql`
  mutation ToggleSavedListing($listingId: ID!) {
    toggleSavedListing(listingId: $listingId) {
      saved
    }
  }
`;

const DELETE_SAVED_SEARCH_MUTATION = gql`
  mutation DeleteSavedSearch($id: ID!) {
    deleteSavedSearch(id: $id) {
      success
    }
  }
`;

type Tab = "games" | "listings" | "searches";
const TABS: { value: Tab; label: string }[] = [
  { value: "games", label: "Žaidimai" },
  { value: "listings", label: "Skelbimai" },
  { value: "searches", label: "Paieškos" },
];

function describeSearchParams(rawParams: string): string {
  const url = new URLSearchParams(rawParams);
  const parts: string[] = [];
  const q = url.get("q");
  if (q) parts.push(`„${q}“`);
  const min = url.get("minPrice");
  const max = url.get("maxPrice");
  if (min || max) parts.push(`${min || "0"}–${max || "∞"} €`);
  const conditions = url.get("conditions");
  if (conditions) parts.push(conditions.split(",").join(", "));
  const cities = url.get("cities");
  if (cities) parts.push(cities);
  const delivery = url.get("delivery");
  if (delivery === "delivery") parts.push("su pristatymu");
  if (delivery === "pickup") parts.push("atsiėmimas");
  const genres = url.get("genres");
  if (genres) parts.push(genres);
  const languages = url.get("languages");
  if (languages) parts.push(languages);
  const sort = url.get("sort");
  if (sort && sort !== "newest") parts.push(`rikiavimas: ${sort}`);
  if (parts.length === 0) return "Visos paieškos sąlygos";
  return parts.join(" · ");
}

export function WishlistsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRaw = searchParams.get("tab") || "games";
  const tab: Tab = TABS.some((t) => t.value === tabRaw) ? (tabRaw as Tab) : "games";
  const setTab = (value: Tab) => setSearchParams({ tab: value }, { replace: true });

  const { data, isLoading } = useQuery({
    queryKey: ["tracker"],
    queryFn: () =>
      graphqlClient.request<{
        wishlists: Wishlist[];
        mySavedListings: Listing[];
        mySavedSearches: SavedSearch[];
      }>(TRACKER_QUERY),
  });

  const wishlists = data?.wishlists ?? [];
  const savedListings = data?.mySavedListings ?? [];
  const savedSearches = data?.mySavedSearches ?? [];

  const counts = useMemo(
    () => ({
      games: wishlists.length,
      listings: savedListings.length,
      searches: savedSearches.length,
    }),
    [wishlists.length, savedListings.length, savedSearches.length]
  );

  const removeWishlist = useMutation({
    mutationFn: (gameId: string) =>
      graphqlClient.request(TOGGLE_WISHLIST_MUTATION, { gameId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
      queryClient.invalidateQueries({ queryKey: ["wishlists"] });
    },
  });

  const toggleNotify = useMutation({
    mutationFn: (vars: { id: string; notifyOnListing: boolean }) =>
      graphqlClient.request(UPDATE_WISHLIST_MUTATION, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
  });

  const removeSavedListing = useMutation({
    mutationFn: (listingId: string) =>
      graphqlClient.request(TOGGLE_SAVED_LISTING_MUTATION, { listingId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
  });

  const removeSavedSearch = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request(DELETE_SAVED_SEARCH_MUTATION, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
      queryClient.invalidateQueries({ queryKey: ["mySavedSearches"] });
    },
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Mano stebėjimai</h1>

      <div className="mb-6 flex gap-1 border-b border-gray-200">
        {TABS.map((option) => {
          const active = tab === option.value;
          const count = counts[option.value];
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setTab(option.value)}
              className={`relative px-4 py-2.5 text-sm font-semibold transition-colors ${
                active
                  ? "text-primary-800"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {option.label}
              <span
                className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                  active ? "bg-primary-100 text-primary-800" : "bg-gray-100 text-gray-600"
                }`}
              >
                {count}
              </span>
              {active && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-800" />
              )}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : tab === "games" ? (
        wishlists.length === 0 ? (
          <EmptyState
            title="Norų sąraše nėra žaidimų"
            description="Pridėk žaidimų, kad gautum pranešimą, kai pasirodys naujų skelbimų."
            icon={
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            }
            action={
              <Link to="/games" className="btn-primary">
                Naršyti žaidimus
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {wishlists.map((wishlist) => {
              const game = wishlist.game;
              const imageUrl = game.imageUrl || game.thumbnailUrl;
              return (
                <div key={wishlist.id} className="card overflow-hidden">
                  <Link to={`/games/${game.id}`}>
                    <div className="aspect-square overflow-hidden bg-gray-100">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={game.title}
                          className="h-full w-full object-contain transition-transform hover:scale-105"
                        />
                      ) : (
                        <GamePlaceholder />
                      )}
                    </div>
                  </Link>
                  <div className="p-4">
                    <Link
                      to={`/games/${game.id}`}
                      className="block truncate text-sm font-semibold text-gray-900 hover:text-primary-800"
                    >
                      {game.title}
                    </Link>
                    {game.yearPublished && (
                      <p className="mt-0.5 text-xs text-gray-500">{game.yearPublished}</p>
                    )}
                    <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
                      {wishlist.maxPrice != null && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Maks. kaina</span>
                          <span className="font-semibold text-gray-900">
                            {wishlist.maxPrice.toFixed(2)} &euro;
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Skelbimai</span>
                        <span className="text-gray-900">
                          {game.activeListingsCount}
                          {game.lowestPrice != null && (
                            <span className="ml-1 text-primary-800">
                              (nuo {game.lowestPrice.toFixed(2)} &euro;)
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={wishlist.notifyOnListing}
                          onChange={() =>
                            toggleNotify.mutate({
                              id: wishlist.id,
                              notifyOnListing: !wishlist.notifyOnListing,
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-primary-800 focus:ring-primary-800"
                        />
                        <span className="text-xs text-gray-600">Pranešimai</span>
                      </label>
                      <button
                        onClick={() => removeWishlist.mutate(game.id)}
                        disabled={removeWishlist.isPending}
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Pašalinti
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : tab === "listings" ? (
        savedListings.length === 0 ? (
          <EmptyState
            title="Įsimintų skelbimų nėra"
            description="Įsimink konkretų skelbimą, kad galėtum vėliau prie jo grįžti."
            icon={
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            }
            action={
              <Link to="/listings" className="btn-primary">
                Naršyti skelbimus
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {savedListings.map((listing) => (
              <div key={listing.id} className="relative">
                <ListingCard listing={listing} />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeSavedListing.mutate(listing.id);
                  }}
                  className="absolute right-2 top-2 z-10 rounded-full bg-white/90 p-1.5 text-gray-600 shadow-sm transition-colors hover:bg-rose-50 hover:text-rose-700"
                  title="Pašalinti iš įsimintų"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )
      ) : savedSearches.length === 0 ? (
        <EmptyState
          title="Stebimų paieškų nėra"
          description="Filtruok skelbimus ir paspausk „Stebėti šią paiešką“ — gausi pranešimą, kai pasirodys naujas tinkamas skelbimas."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
          action={
            <Link to="/listings" className="btn-primary">
              Naršyti skelbimus
            </Link>
          }
        />
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
          {savedSearches.map((search) => (
            <li key={search.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/listings?${search.params}`}
                  className="block truncate text-sm font-semibold text-gray-900 hover:text-primary-800"
                >
                  {search.name || describeSearchParams(search.params)}
                </Link>
                <p className="mt-0.5 truncate text-xs text-gray-500">
                  Pridėta {new Date(search.createdAt).toLocaleDateString("lt-LT")}
                </p>
              </div>
              <Link
                to={`/listings?${search.params}`}
                className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Atidaryti
              </Link>
              <button
                type="button"
                onClick={() => removeSavedSearch.mutate(search.id)}
                disabled={removeSavedSearch.isPending}
                className="shrink-0 text-sm font-medium text-red-600 hover:text-red-700"
                title="Pašalinti paiešką"
              >
                Pašalinti
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
